import { useState } from "react";
import { useDeleteProjectMutation } from "../../../../features/projectApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { useAppDispatch } from "../../../lib/redux/hooks";
import { ConfirmModal } from "../../../ui/organisms/confirmModal.tsx";
import { Pagination } from "../../../ui/organisms/pagination";
import { FindAllProjectsFilters, FindAllProjectsResponse } from "../dto";
import { ProjectTable } from "./table";
import { UserDTO } from "../../users/dto";

interface Props {
  projects: FindAllProjectsResponse
  teamLeaders: { user: UserDTO, avatar: string }[]
  commercials: { user: UserDTO, avatar: string }[]
  filters: FindAllProjectsFilters
  setFilters: (filters: Partial<FindAllProjectsFilters>) => void
}

export function ProjectsList({ projects, filters, setFilters, teamLeaders, commercials }: Props) {
  const dispatch = useAppDispatch();
  const [idToDelete, setIdToDelete] = useState<string | undefined>();
  const [deleteProject] = useDeleteProjectMutation();

  const handleDeleteProject = async () => {
    try {
      await deleteProject({ projectId: idToDelete! }).unwrap();
      setIdToDelete(undefined);
      dispatch(addBannerWithTimeout({
        message: 'Progetto eliminato con successo.',
        variant: 'success'
      }))
    } catch (error) {
      const errors = error as { status: number, data: { errors: { message: string }[] } };
      let message = "Si è verificato un errore durante l'eliminazione del progetto.";
      if (errors.data.errors.map(e => e.message).includes("Cannot delete project with target lists")) {
        message = "Impossibile eliminare il progetto perché sono presenti liste target associate.";
      }
      dispatch(addBannerWithTimeout({
        message,
        variant: 'error'
      }))
    }
  }

  return (
    <div className="w-[100%] h-full max-h-full flex flex-col"
      style={{ height: 'calc(100% - 64px)' }}
    >
      <ProjectTable
        teamLeaders={teamLeaders}
        commercials={commercials}
        filters={filters}
        setFilters={(newFilter) => setFilters({ ...filters, ...newFilter, page: 0 })}
        projects={projects}
        setIdToDelete={function (arg?: string): void {
          setIdToDelete(arg)
        }} />
      <Pagination
        itemsPerPage={filters.itemsPerPage}
        setItemsPerPage={value => setFilters({ ...filters, itemsPerPage: value, page: 0 })}
        found={projects?.total || 0}
        pages={projects?.totalPage || 1}
        currentPage={filters.page}
        setPage={(page) => setFilters({ ...filters, page })}
      />
      <ConfirmModal
        title={"Elimina progetto"}
        label={"Sei sicuro di voler eliminare questo progetto?"}
        confirmAction={function (): void {
          idToDelete && handleDeleteProject();
        }}
        onClose={function (): void {
          setIdToDelete(undefined);
        }}
        visible={idToDelete !== undefined}
      />
    </div>
  );
}
